import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BaseModal } from "../BaseModal";
import { Button, ButtonThemeEnum } from "../Buttons/styles";
import { OFACWrapper } from "./style";
import { LocalStorageKeyEnum } from "../../enums/localStorage";
import { ModalKeyEnum } from "../../portal";
import { isPWA } from "../../utils/pwa";
import { usePWAFlow } from "../../hooks/usePWAFlow";

// When the page is opened with ?agent=1, we want the agent gate even after
// React Router navigates (e.g. OutletRoute -> navigate(newPath, { replace: true }))
// because navigate(path) drops `?agent=1` from `window.location.search`. Cache the
// flag in sessionStorage on the first read so the choice survives any in-tab
// route change. Lazy useState initializer runs once per mount, no flicker.
const AGENT_SESSION_KEY = "agent-mode";
function readAgentModeOnce(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const fromUrl = new URLSearchParams(window.location.search).get("agent") === "1";
    if (fromUrl) window.sessionStorage.setItem(AGENT_SESSION_KEY, "1");
    return fromUrl || window.sessionStorage.getItem(AGENT_SESSION_KEY) === "1";
  } catch {
    // sessionStorage can throw in private/incognito or sandboxed contexts.
    try {
      return new URLSearchParams(window.location.search).get("agent") === "1";
    } catch {
      return false;
    }
  }
}

function OFACModal() {
  const [termsAccepted, setTermsAccepted] = useState<boolean>(true);
  const { t } = useTranslation("app", { keyPrefix: "OFACModal" });

  // ?agent=1 in the query string (or remembered for the current tab session)
  // shows a gate worded for an automated agent to attest on its own behalf,
  // since it can't honestly attest a human's residency or VPN use. Same accept
  // action + storage as the human gate, so the site stays usable for both.
  const [isAgentMode] = useState<boolean>(readAgentModeOnce);
  const titleKey = isAgentMode ? "title_agent" : "title";
  const introKey = isAgentMode ? "ofac_agent_desc_1" : "ofac_desc_1";
  const desc2Key = isAgentMode ? "ofac_agent_desc_2" : "ofac_desc_2";
  const desc3Key = isAgentMode ? "ofac_agent_desc_3" : "ofac_desc_3";

  const { pwaInfo } = usePWAFlow();

  // Initial check to see whether terms have already been accepted
  useEffect(() => {
    const ofac = window.localStorage.getItem(LocalStorageKeyEnum.OFAC);
    setTermsAccepted(Boolean(ofac));
  }, []);

  const setTerms = useCallback(() => {
    window.localStorage.setItem(LocalStorageKeyEnum.OFAC, "true");
    setTermsAccepted(true);
  }, [setTermsAccepted]);

  if (pwaInfo && isPWA && termsAccepted) {
    return null;
  }

  return (
    <BaseModal
      id={ModalKeyEnum.OFAC}
      show={!termsAccepted}
      title={t(titleKey)}
      style={{ width: 311 }}
    >
      <OFACWrapper>
        <p>{t(introKey)}:</p>
        <ul>
          <li>{t(desc2Key)}</li>
          <li>{t(desc3Key)}</li>
        </ul>
        <Button
          fullWidth
          buttonTheme={ButtonThemeEnum.NEUTRAL2}
          onClick={setTerms}
        >
          {t("continue")}
        </Button>
      </OFACWrapper>
    </BaseModal>
  );
}

export default OFACModal;
